import {
  Alert,
  Container,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { Phone, MessageCircle, MessageSquareText } from "lucide-react";
import useApiEp from "../hooks/useApiEp";
import MuPb from "../widgets/MuPb";
import { getErrorResponse } from "../utils/util";

const UnifiedCampaign = () => {
  const [selectedGroup, setSelectedGroup] = useState(null);

  // 1. Fetch initial contact list
  const {
    data: contactList,
    isLoading: loadingList,
    isError: isContactsError,
    error: contactsError,
  } = useApiEp(`/user/group-list`);

  // 2. Fetch group count conditionally based on selection
  // Note: Adjust the endpoint path to match your actual backend route for fetching counts
  const { data: groupDetails, isLoading: loadingCount } = useApiEp(
    `/user/group-list-count?groupName=${selectedGroup}`,
    !!selectedGroup // Query is only enabled when selectedGroup is truthy
  );

  if (loadingList) {
    return <MuPb />;
  }

  if (isContactsError) {
    return <Alert severity="error">{getErrorResponse(contactsError)}</Alert>;
  }

  const campaignOptions = [
    { title: "Voice Campaign", icon: <Phone size={36} color="#1976d2" /> },
    {
      title: "WhatsApp Campaign",
      icon: <MessageCircle size={36} color="#25D366" />,
    },
    {
      title: "RCS Campaign",
      icon: <MessageSquareText size={36} color="#f57c00" />,
    },
  ];

  return (
    <Container sx={{ mt: 3, maxWidth: "900px" }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        New Campaign
      </Typography>

      <Autocomplete
        size="small"
        options={contactList || []}
        value={selectedGroup}
        onChange={(event, newValue) => {
          setSelectedGroup(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Contact Group"
            variant="outlined"
          />
        )}
        sx={{ mb: 4, maxWidth: "760px" }}
      />

      {/* Only show the options and count if a group is selected */}
      {selectedGroup && (
        <Box sx={{ animation: "fadeIn 0.3s ease-in-out" }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              Selected Group: <strong>{selectedGroup}</strong>
            </Typography>

            {/* Display loader while fetching count, or display the count once available */}
            {loadingCount ? (
              <CircularProgress size={16} />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  bgcolor: "action.hover",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                }}
              >
                {(groupDetails?.count || 0).toLocaleString("en-IN")} Contacts
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {campaignOptions.map((option, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card
                  elevation={2}
                  sx={{
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardActionArea
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {option.icon}
                    <Typography variant="subtitle1" fontWeight="500">
                      {option.title}
                    </Typography>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
  );
};

export default UnifiedCampaign;
